import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import styles from './ConversationSheet.module.css'
import {
  lastSheetTrigger,
  lockPageScroll,
  scrolledFrom,
  sheetKeyDown,
  sheetOpener,
  swipeCloses,
} from './sheet-helpers'

export type ConversationSheetVariant = 'drawer' | 'sheet'

export interface ConversationSheetProps {
  open: boolean
  onClose: () => void
  /**
   * The CALLER picks it by breakpoint (`lg ? 'drawer' : 'sheet'`) — this component never
   * overrides it from CSS (cb13b6ae: a variant by prop, not by a className media query).
   */
  variant: ConversationSheetVariant
  /** The id of the element that names the panel (the panel's `<h2>`); "Conversation" until it
   *  exists (or without one). */
  labelledBy?: string
  children: React.ReactNode
}

/**
 * The bottom of the site header — the drawer's top. Read when the drawer opens, on resize, on
 * every scroll and when the header's slide ends: AA and SG headers differ in height, and both
 * hide on a scroll down (sticky at a negative top, translated back in on a scroll up). A header
 * scrolled away reads ≤ 0 and the drawer goes to the viewport top: it then covers the rail card
 * stuck at the stack line, which with the old 96px fallback showed its header above the
 * drawer — two "Conversation" headers, one over the other (Guillaume, 2026-10-30, AA). Without a
 * header at all the CSS fallback stands.
 */
export function measuredDrawerTop(
  header: Pick<Element, 'getBoundingClientRect'> | null | undefined
): number | null {
  const bottom = header?.getBoundingClientRect().bottom
  return bottom !== undefined ? Math.max(0, Math.ceil(bottom)) : null
}

const OpenSheet: React.FC<Omit<ConversationSheetProps, 'open'>> = ({
  onClose,
  variant,
  labelledBy,
  children,
}) => {
  const box = useRef<HTMLDivElement>(null)
  // The latest onClose, so the listeners below never re-bind on a parent render.
  const close = useRef(onClose)
  close.current = onClose
  // The control that opened the panel, read on the first render — before a child's autoFocus
  // (the composer) moves focus inside. Mounted only after a press or a deep link, in a browser.
  const opener = useRef<Element | null>(null)
  if (opener.current === null) {
    opener.current = sheetOpener(document.activeElement, lastSheetTrigger(), document.body)
  }

  // Focus in on open, back to the opener on close — once per open, whatever the variant does.
  useEffect(() => {
    const panel = box.current
    const back = opener.current
    const frame = window.requestAnimationFrame(() => {
      if (panel && !panel.contains(document.activeElement)) {
        panel.focus({ preventScroll: true })
      }
    })
    return () => {
      window.cancelAnimationFrame(frame)
      if (back instanceof HTMLElement && back.isConnected && back !== document.body) {
        back.focus({ preventScroll: true })
      }
    }
  }, [])

  // The variant's own behaviour; re-runs if the reader crosses lg with the panel open.
  useEffect(() => {
    const panel = box.current
    if (!panel) {
      return undefined
    }
    if (variant === 'sheet') {
      const restore = lockPageScroll(document)
      const onKey = (e: KeyboardEvent): void => sheetKeyDown(e, panel, () => close.current(), true)
      panel.addEventListener('keydown', onKey)
      return () => {
        restore()
        panel.removeEventListener('keydown', onKey)
      }
    }
    // Drawer: focus may be anywhere on the page, so Escape is heard document-wide — unless
    // something else already handled it or it came from inside another modal dialog.
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented) {
        return
      }
      const target = e.target instanceof Element ? e.target : null
      const modal = target?.closest('[aria-modal="true"]')
      if (modal && modal !== panel) {
        return
      }
      e.preventDefault()
      close.current()
    }
    const header = document.querySelector('header')
    const measure = (): void => {
      const top = measuredDrawerTop(header)
      if (top === null) {
        panel.style.removeProperty('--conversation-drawer-top-measured')
      } else {
        panel.style.setProperty('--conversation-drawer-top-measured', `${top}px`)
      }
    }
    // The drawer is not modal — the page scrolls under it and the header slides out and back in
    // (a 0.2 s transform transition). One measure per frame while scrolling, and one more when
    // the slide ends, keep the drawer's top on the header's bottom.
    let frame = 0
    const onScroll = (): void => {
      if (!frame) {
        frame = window.requestAnimationFrame(() => {
          frame = 0
          measure()
        })
      }
    }
    measure()
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', onScroll, { passive: true })
    header?.addEventListener('transitionend', measure)
    return () => {
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', onScroll)
      header?.removeEventListener('transitionend', measure)
      if (frame) {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [variant])

  // Swipe down (sheet only): from the handle, or anywhere on the panel while nothing under the
  // finger is scrolled down — the community BottomSheet's gesture, same thresholds.
  const drag = useRef<{ y: number; t: number; dy: number } | null>(null)
  const onTouchStart = (e: React.TouchEvent): void => {
    const panel = box.current
    const fromHandle = (e.target as HTMLElement).closest?.(`.${styles.handle}`)
    if (
      variant !== 'sheet' ||
      !panel ||
      e.touches.length !== 1 ||
      (!fromHandle && scrolledFrom(e.target as Element, panel))
    ) {
      drag.current = null
      return
    }
    drag.current = { y: e.touches[0].clientY, t: Date.now(), dy: 0 }
  }
  const onTouchMove = (e: React.TouchEvent): void => {
    const panel = box.current
    if (!drag.current || !panel) {
      return
    }
    const dy = Math.max(0, e.touches[0].clientY - drag.current.y)
    drag.current.dy = dy
    panel.style.transform = dy > 0 ? `translateY(${dy}px)` : ''
  }
  const onTouchEnd = (): void => {
    const panel = box.current
    const done = drag.current
    drag.current = null
    if (!panel || !done) {
      return
    }
    if (swipeCloses(done.dy, Date.now() - done.t)) {
      close.current()
    } else {
      panel.style.transform = ''
    }
  }

  const sheet = variant === 'sheet'
  // The backdrop closes on its own click — closing on pointerdown would unmount it and drop the
  // click on whatever lies underneath.
  return createPortal(
    <>
      {sheet && (
        <div className={styles.backdrop} onClick={() => close.current()} aria-hidden="true" />
      )}
      <div
        role="dialog"
        aria-modal={sheet ? 'true' : undefined}
        aria-labelledby={labelledBy}
        // Always set too: the labelling <h2> arrives with the lazy panel chunk, after focus moves
        // in — until it resolves, the dialog is still named.
        aria-label="Conversation"
        tabIndex={-1}
        data-variant={variant}
        className={clsx(styles.panel, sheet ? styles.sheet : styles.drawer)}
        ref={box}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {sheet && <span className={styles.handle} aria-hidden="true" />}
        {children}
      </div>
    </>,
    document.body
  )
}

/**
 * The article conversation's one overlay (comments always in reach, decision 3), both brands.
 *
 * - `sheet` (below lg): modal. Dimmed backdrop (tap closes), a panel from the bottom stopping
 *   ~70px under the top, 16px top corners and a drag handle; the panel is a flex column and the
 *   caller's child picks what scrolls (`flex: 1 1 auto; min-height: 0; overflow-y: auto`). The
 *   page is locked (`lockPageScroll`, <html> + <body>), Tab is trapped, Escape and a swipe down
 *   (`scrolledFrom` / `swipeCloses` — the community BottomSheet's steps) close it.
 * - `drawer` (lg and up): NOT modal. No backdrop, no lock, no trap — the article still scrolls
 *   and stays clickable. Fixed to the right edge, from under the sticky header
 *   (`--conversation-drawer-top`, else the header's measured bottom — followed through scroll,
 *   0 while the header is scrolled away — else 96px) to the viewport bottom. Escape closes.
 *
 * Both take focus on open (after the frame, unless a child already took it — the composer) and
 * hand it back to the opener on close; the opener is read during render (Constraint 373eb31c),
 * with the WebKit-tap fallback (6ead9e26). Rendered only while open, in a portal to <body> —
 * never in the server HTML. z: backdrop 1000002, panel 1000003 — over the sticky header
 * (1000001), under PopupWrapper and the notifications layer.
 */
const ConversationSheet: React.FC<ConversationSheetProps> = ({ open, ...props }) => {
  if (!open || typeof document === 'undefined') {
    return null
  }
  return <OpenSheet {...props} />
}

export default ConversationSheet
